import React, { useEffect, useLayoutEffect } from 'react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { OpPage } from 'new-components/OpPage/OpPage';
import { appColorThemeManager } from 'utils/appColorThemeManager';
import { OpApp } from 'new-components/DLS/OpApp/OpApp';
import { setAlert } from 'routes/AppContainer/actions';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { AlertWrapper } from 'containers/AlertWrapper/AlertWrapper';
import { createErrorAlertArgs } from 'utils/alertHelpers';

import { useAltaLoginUrlParams } from '../hooks/useAltaLoginUrlParams';
import { isValidAbsoluteRedirectUrl } from '../helpers/isValidAbsoluteRedirectUrl';
import { isValidRelativePath } from '../helpers/isValidRelativeUrl';

import { LanguageSelectorNotAuthenticated } from './LanguageSelectorNotAuthenticated/LanguageSelectorNotAuthenticated';
import { Marketing } from './Marketing/Marketing';
import { UnifiedLogin } from './UnifiedLogin/UnifiedLogin';
import { LoginSSOAltaIdForm } from './LoginSSOAltaIdForm/LoginSSOAltaIdForm';

import './App.scss';

/**
 * @component
 * @description Component that wraps the altaLoginApp and holds the top level functionality.
 * Gathers the url params and figures out what functions to call and pages to render based on these params.
 */
export const App = () => {
  const dispatch = useDispatch();
  const { redirectUrl } = useAltaLoginUrlParams();

  // If the redirectUrl isn't valid then disregard it. We don't need errors exposed
  const finalRedirectUrl =
    isValidAbsoluteRedirectUrl(redirectUrl) || isValidRelativePath(redirectUrl)
      ? redirectUrl
      : '';

  const { hasPortalAccess, useAltaId } = useUrlSearchParams();
  const isAltaIdForm = useAltaId === 'true';

  // Create a react-query client
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      // Catch any react-query useQuery error globally and display the alert (this will not catch useMutation errors)
      onError: (error) => {
        const typedError = error as unknown as Error & {
          suppressErrorMessage: boolean;
        };

        if (!typedError.suppressErrorMessage) {
          dispatch(
            setAlert(
              // @ts-expect-error TS doesn't like the extra field we pass to the error
              ...createErrorAlertArgs({ alertMessage: error.message, error }),
            ),
          );
        }
      },
    }),
  });

  useLayoutEffect(() => {
    // Save the current theme so we can set it back before unmount
    const previousTheme = appColorThemeManager.getCurrentElColorTheme();
    appColorThemeManager.handleThemeToggle('dark');
    return () => {
      appColorThemeManager.handleThemeToggle(previousTheme);
    };
  }, []);

  useEffect(() => {
    if (hasPortalAccess === 'false') {
      dispatch(
        setAlert(
          'error',
          'Oops! Your user profile currently has no permission to access this portal. If you feel this is in error, please contact an administrator for your Avigilon Alta account.',
        ),
      );
    }
  }, [dispatch, hasPortalAccess]);

  return (
    <OpApp>
      <OpPage className="App">
        <div className="App__left">
          <Marketing />
        </div>
        <div className="App__right">
          <div className="App__container">
            <div className="App__logo">
              <img
                alt="Avigilon Alta"
                src={require('assets/img/avigilon-alta-logo-dark.svg')}
              />
            </div>
            <QueryClientProvider client={queryClient}>
              {isAltaIdForm ? (
                <LoginSSOAltaIdForm
                  redirectUrl={redirectUrl}
                  loginRoute="/signin"
                />
              ) : (
                <UnifiedLogin redirectUrl={finalRedirectUrl} />
              )}
              <AlertWrapper />
            </QueryClientProvider>
            <LanguageSelectorNotAuthenticated />
          </div>
        </div>
      </OpPage>
    </OpApp>
  );
};

export default App;
